import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api';
import { DISCLAIMER, Logo } from '../components/HeaderBits';
import { PasswordField } from '../components/ui';
import { useLive } from '../hooks/useLive';

export default function Login() {
  const { me, meLoaded, login } = useLive();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from;
  const target = from && from !== '/login' ? from : '/';

  if (meLoaded && me) return <Navigate to={target} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter your username and password.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError('Invalid username or password.');
      else if (err instanceof ApiError && err.status === 429) setError('Too many sign-in attempts. Please wait a minute and try again.');
      else setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 16,
        background: 'radial-gradient(ellipse at top, #0f1d36 0%, #070d18 60%)',
      }}
    >
      <form
        onSubmit={submit}
        className="panel"
        aria-labelledby="login-title"
        style={{ width: '100%', maxWidth: 400, padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <Logo large />
        <h1 id="login-title" className="cond" style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }}>
          Sign in
        </h1>
        {error && (
          <div className="err-box" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="lbl">Username</span>
          <input
            className="fld"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{ height: 42 }}
          />
        </label>
        <PasswordField
          label="Password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
        />
        <button type="submit" className="btn btn-primary hov" disabled={busy} style={{ height: 44, fontSize: 15 }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p style={{ maxWidth: 400, margin: 0, fontSize: 12.5, lineHeight: 1.45, color: '#93a5c1', textAlign: 'center' }}>
        {DISCLAIMER}
      </p>
    </div>
  );
}
